import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { filmstrip as filmstripGeometry } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { ErrorNote, Eyebrow, Spinner, timecode } from "../components/ui.js";
import { api, type CutReviewData, type CutWindow } from "../api.js";
import { nudgeJobActivity, useJobActivity, useOnJobSettled } from "../lib/jobActivity.js";

/** How far a nudge moves a window. A frame is too fine to aim at by button. */
const NUDGE_SECONDS = 0.5;

/** Below this the model was guessing, and the row is worth looking at first. */
const LOW_CONFIDENCE = 0.5;

const ANCHOR_LABELS: Record<string, string> = {
  // Where the window came from. "model" and "model-adjusted" are different
  // facts: one arrived usable, the other had to be pulled back inside the scene
  // or stretched to carry its line, and that is worth a look.
  model: "chosen by the model",
  "model-adjusted": "chosen by the model, corrected to fit",
  "scene-start": "the start of the scene",
  action: "a click",
  "screen-change": "the picture changing",
  "most-relevant": "what the analysis rated highest",
};

/**
 * A strip of frames across one scene, with the kept window marked on it.
 *
 * Drawn from the filmstrip sheet the editor already uses rather than by pulling
 * frames of its own: one contact sheet exists per project, and a second way of
 * getting pictures out of a recording would be a second thing to keep in step.
 */
const SceneStrip: React.FC<{
  window: CutWindow;
  filmstrip: CutReviewData["filmstrip"];
  onScrub: (start: number) => void;
}> = ({ window, filmstrip, onScrub }) => {
  const span = Math.max(0.001, window.sceneEnd - window.sceneStart);
  const left = ((window.start - window.sceneStart) / span) * 100;
  const width = ((window.end - window.start) / span) * 100;

  // One cell per filmstrip interval across this scene, indexed into the sheet.
  const cells = useMemo(() => {
    if (!filmstrip) return [];
    const { intervalSeconds, columns, cellWidth } = filmstripGeometry;
    const count = Math.max(1, Math.min(24, Math.round(span / intervalSeconds)));
    return Array.from({ length: count }, (_, i) => {
      const at = window.sceneStart + (i / count) * span;
      const index = Math.floor(at / intervalSeconds);
      const cellHeight = Math.round((cellWidth * filmstrip.height) / filmstrip.width) || cellWidth;
      return {
        at,
        x: (index % columns) * cellWidth,
        y: Math.floor(index / columns) * cellHeight,
        cellHeight,
      };
    });
  }, [filmstrip, span, window.sceneStart]);

  return (
    <div className="relative mt-2 h-[54px] overflow-hidden rounded-[5px] border border-[var(--edge)] bg-bay-900">
      <div className="flex h-full">
        {cells.map((cell, index) => (
          <button
            key={index}
            type="button"
            title={`Move the window here — ${timecode(cell.at)}`}
            onClick={() => onScrub(cell.at)}
            className="h-full flex-1 bg-cover opacity-70 transition-opacity hover:opacity-100"
            style={
              filmstrip
                ? {
                    backgroundImage: `url(/assets/${filmstrip.key})`,
                    backgroundPosition: `-${cell.x}px -${cell.y}px`,
                    backgroundSize: `${filmstrip.width}px ${filmstrip.height}px`,
                  }
                : undefined
            }
          />
        ))}
      </div>

      {/* The rejected windows, drawn so the choice reads as a choice rather
          than as the only thing that was ever on the table. */}
      {window.candidates
        .filter((candidate) => Math.abs(candidate.start - window.start) > 0.05)
        .map((candidate) => (
          <span
            key={candidate.id}
            title={`Offered: ${timecode(candidate.start)} — ${ANCHOR_LABELS[candidate.anchor] ?? candidate.anchor}`}
            className="pointer-events-none absolute top-0 h-full border-x border-dashed border-ink-400/50"
            style={{
              left: `${((candidate.start - window.sceneStart) / span) * 100}%`,
              width: `${((candidate.end - candidate.start) / span) * 100}%`,
            }}
          />
        ))}

      <span
        className="pointer-events-none absolute top-0 h-full border-2 border-ember-500 bg-ember-500/20"
        style={{ left: `${Math.max(0, left)}%`, width: `${Math.min(100, width)}%` }}
      />
    </div>
  );
};

/**
 * Reviewing the cut a silent recording was given.
 *
 * A narration-fitted cut is a series of judgements — for each scene, which few
 * seconds of it the line is actually describing — and each one can be wrong in
 * a way only somebody watching can see. The reasons and the rejected windows
 * are shown beside the choice, because a cut nobody can explain is one nobody
 * can correct.
 */
export const CutReview: React.FC = () => {
  const { id = "" } = useParams();
  const [data, setData] = useState<CutReviewData | null>(null);
  const [windows, setWindows] = useState<CutWindow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Work already running for this project.
  //
  // `busy` only covers the request, which returns 202 the moment the job is
  // queued — so both buttons re-enabled while the job had not even started, and
  // an impatient second press queued a second one. Ten replans in four seconds
  // is six model calls for one answer.
  const { active } = useJobActivity();
  const working = active.some((job) => job.projectId === id);

  const load = useCallback(async () => {
    try {
      const result = await api.cutReview(id);
      setData(result);
      setWindows(result.windows);
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // A finalise or a re-plan rewrites the very windows this screen is showing.
  useOnJobSettled(() => void load(), id);

  const update = (sceneId: string, mutate: (window: CutWindow) => CutWindow) =>
    setWindows((current) => current.map((window) => (window.sceneId === sceneId ? mutate(window) : window)));

  /** Moves a window without changing its length or letting it leave its scene. */
  const moveTo = (window: CutWindow, start: number): CutWindow => {
    const length = window.end - window.start;
    const clamped = Math.max(window.sceneStart, Math.min(start, window.sceneEnd - length));
    return { ...window, start: Number(clamped.toFixed(3)), end: Number((clamped + length).toFixed(3)) };
  };

  const dirty = useMemo(() => JSON.stringify(windows) !== JSON.stringify(data?.windows ?? []), [windows, data]);
  // Whether any window actually moved, as opposed to only being pinned. The
  // button says which of the two it is about to do.
  const moved = useMemo(() => {
    const before = data?.windows ?? [];
    return windows.some((window, index) => {
      const was = before[index];
      return !was || Math.abs(was.start - window.start) > 0.01 || Math.abs(was.end - window.end) > 0.01;
    });
  }, [windows, data]);

  const kept = useMemo(() => windows.reduce((total, window) => total + (window.end - window.start), 0), [windows]);

  if (error) {
    return (
      <AppShell>
        <div className="p-6">
          <ErrorNote>{error}</ErrorNote>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-[13px] text-ink-300">
          <Spinner /> Loading the cut…
        </div>
      </AppShell>
    );
  }

  if (windows.length === 0) {
    return (
      <AppShell>
        <div className="mx-auto max-w-[900px] p-6">
          <h1 className="text-[20px] font-semibold text-ink-100">Cut review</h1>
          <p className="mt-2 text-[13px] text-ink-300">
            This project has no narration-fitted cut. It appears when a recording with no commentary is cut down to the
            length of the script written for it.
          </p>
          <Link className="btn btn-secondary btn-sm mt-4 inline-flex" to={`/projects/${id}/editor`}>
            Back to the editor
          </Link>
        </div>
      </AppShell>
    );
  }

  const uncertain = windows.filter((window) => window.confidence !== null && window.confidence < LOW_CONFIDENCE).length;
  // Rows the model did not decide: it gave no answer for them, or gave one that
  // could not be used. Either way they are arithmetic, and worth checking first.
  const unmoved = windows.filter((window) => !window.anchor.startsWith("model")).length;

  return (
    <AppShell>
      <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 p-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Eyebrow>Cut review</Eyebrow>
            <h1 className="mt-1 text-[20px] font-semibold text-ink-100">Which few seconds each line plays over</h1>
            <p className="tnum mt-1 text-[12px] text-ink-400">
              {timecode(kept)} kept of {timecode(data.durationSeconds)} · {windows.length} scenes
              {data.decidedBy ? ` · chosen by ${data.decidedBy}` : null}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link className="btn btn-secondary btn-sm" to={`/projects/${id}/editor`}>
              Editor
            </Link>
            <button
              className="btn btn-secondary btn-sm"
              disabled={busy !== null || working}
              title={working ? "Something is already running for this project" : "Choose the windows again"}
              onClick={() => {
                setBusy("replan");
                setNote(null);
                void api
                  .replanCut(id)
                  .then(() => {
                    setNote("Queued. The bar above shows it running.");
                    nudgeJobActivity();
                  })
                  .catch((cause: Error) => setNote(cause.message))
                  .finally(() => setBusy(null));
              }}
            >
              {busy === "replan" || working ? "Working…" : "Ask again"}
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={busy !== null || working || !dirty}
              title={
                !dirty
                  ? "Nothing has changed"
                  : moved
                    ? "Store these windows and rebuild the video"
                    : "Save the pins — no window has moved, so the video will not change"
              }
              onClick={() => {
                setBusy("save");
                setNote(null);
                void api
                  .saveCut(
                    id,
                    windows.map((window) => ({
                      sceneId: window.sceneId,
                      start: window.start,
                      end: window.end,
                      pinned: window.pinned,
                    })),
                  )
                  .then((result) => {
                    // Pinning changes nothing about the video, so saying the
                    // video is being rebuilt would be a promise of a change
                    // that never arrives — which is what makes it look broken.
                    setNote(
                      result.rebuilt
                        ? "Finalised. The video is being rebuilt."
                        : "Pins saved. No window moved, so the video is unchanged.",
                    );
                    nudgeJobActivity();
                    return load();
                  })
                  .catch((cause: Error) => setNote(cause.message))
                  .finally(() => setBusy(null));
              }}
            >
              {busy === "save" ? "Saving…" : working ? "Working…" : moved ? "Finalise" : "Save pins"}
            </button>
          </div>
        </header>

        {/* What to look at first, rather than making somebody read every row to
            find the two that need them. */}
        {uncertain + unmoved > 0 ? (
          <p className="rounded-[6px] border border-[var(--edge)] bg-bay-850 px-3 py-2 text-[12px] text-ink-300">
            {uncertain > 0 ? `${uncertain} ${uncertain === 1 ? "scene" : "scenes"} the model was unsure about` : null}
            {uncertain > 0 && unmoved > 0 ? " · " : null}
            {unmoved > 0 ? `${unmoved} the model did not decide` : null}
          </p>
        ) : null}

        {note ? <p className="text-[12px] text-ink-300">{note}</p> : null}

        <ul className="flex flex-col gap-3">
          {windows.map((window, index) => (
            <li key={window.sceneId} className="panel p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-[13px] text-ink-100">
                  <span className="text-ink-400">{index + 1}.</span> “{window.narration.trim() || "no line"}”
                </p>
                <span className="tnum shrink-0 text-[11px] text-ink-400">
                  {timecode(window.start)}–{timecode(window.end)} of scene {timecode(window.sceneStart)}–
                  {timecode(window.sceneEnd)}
                </span>
              </div>

              <SceneStrip
                window={window}
                filmstrip={data.filmstrip}
                onScrub={(start) => update(window.sceneId, (current) => moveTo(current, start))}
              />

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-ink-400">
                  {window.reason ? `“${window.reason}”` : "kept the opening of the scene"}
                  {window.confidence !== null ? (
                    <span className={window.confidence < LOW_CONFIDENCE ? " text-signal-warn" : undefined}>
                      {" "}
                      · {Math.round(window.confidence * 100)}% sure
                    </span>
                  ) : null}
                  {" · anchored on "}
                  {ANCHOR_LABELS[window.anchor] ?? window.anchor}
                </p>

                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    className="btn btn-secondary btn-sm"
                    title={`Move ${NUDGE_SECONDS}s earlier`}
                    onClick={() => update(window.sceneId, (current) => moveTo(current, current.start - NUDGE_SECONDS))}
                  >
                    ◀
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    title={`Move ${NUDGE_SECONDS}s later`}
                    onClick={() => update(window.sceneId, (current) => moveTo(current, current.start + NUDGE_SECONDS))}
                  >
                    ▶
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Keep this whole scene, however long its line is"
                    onClick={() =>
                      update(window.sceneId, (current) => ({
                        ...current,
                        start: current.sceneStart,
                        end: current.sceneEnd,
                      }))
                    }
                  >
                    Whole scene
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    aria-pressed={Boolean(window.pinned)}
                    title="Keep this window when the cut is asked for again"
                    onClick={() => update(window.sceneId, (current) => ({ ...current, pinned: !current.pinned }))}
                  >
                    {window.pinned ? "Pinned" : "Pin"}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
};
